// useInlineDdlBlastRadiusGate — VDE-1. Mirrors the Designer wiring in
// `render/table/TableEditor.tsx:98-131` for the Query-Editor inline DDL
// toolbar so the locked blast-radius guard (PRD VQE-D7 "always on" + VQE-D9
// "inline toolbar still fires the guard") actually fires on inline drops.
//
// `useInlineDdlEditContext` already accepts an optional `blastRadius` and
// spreads it through, but the inline host never supplied one — so
// `commitDdlEdit`'s `if (ctx.blastRadius && isDropOp(edit))` guard was
// skipped and inline drops of constrained objects with dependents committed
// silently. This headless hook resolves the parked table's dependency
// snapshot via the same `useDependencyAnnotations` IPC query the Designer
// uses (it is NOT the Designer-only sidebar UI) and post-hoc spreads a
// `'inline'`-sourced `BlastRadiusGate` onto the context — the same post-hoc
// spread pattern TableEditor uses.
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice H3 + VQE-D7/VQE-D9.
import { useMemo } from 'react';

import type { QualifiedName } from '../../../sql-grammar/model';
import { buildBlastRadiusGate } from '../dependencies/buildBlastRadiusGate';
import { useDependencyAnnotations } from '../dependencies/useDependencyAnnotations';
import type { VqeDdlEditContext } from '../edit/editContext';
import { useVqeDdlStore } from '../store';

function tableQualifiedNameFromContext(
  editContext: VqeDdlEditContext | null,
): QualifiedName | null {
  if (!editContext) return null;
  try {
    const structure = editContext.service.extractDdlStructure(
      editContext.documentId,
      editContext.statementId,
    );
    if (structure.kind === 'create-table' || structure.kind === 'alter-table') {
      return structure.table;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Returns the `editContext` with an `'inline'`-sourced `BlastRadiusGate`
 * spliced in when a connection is bound; otherwise returns the context
 * untouched (`editContext ?? null`). Hook order is unconditional — the
 * dependency query self-gates via its `enabled` flag, so this respects
 * React's rules-of-hooks even when no DDL statement is parked.
 */
export function useInlineDdlBlastRadiusGate(
  editContext: VqeDdlEditContext | null,
): VqeDdlEditContext | null {
  const fromTable = useMemo<QualifiedName | null>(
    () => tableQualifiedNameFromContext(editContext),
    [editContext],
  );

  const connectionBound = !!(editContext?.connectionId && editContext?.database);
  const gateActive = !!editContext && connectionBound;

  // Always-on dependency annotations. The hook's `enabled` gate keeps the
  // IPC quiet until a connection is bound + the parked object is named.
  const dep = useDependencyAnnotations({
    connectionId: editContext?.connectionId,
    database: editContext?.database,
    schema: fromTable?.schema ?? null,
    objectName: fromTable?.name ?? null,
    objectType: 'table',
    enabled: gateActive,
  });

  const annotations = dep.annotations;
  const setPendingBlastRadius = useVqeDdlStore((s) => s.setPendingBlastRadius);

  return useMemo<VqeDdlEditContext | null>(() => {
    if (!editContext || !gateActive) return editContext ?? null;
    return {
      ...editContext,
      // Parity with the Designer site is now structural — both construct the
      // gate via the shared `buildBlastRadiusGate` factory.
      blastRadius: buildBlastRadiusGate({
        source: 'inline',
        annotations,
        annotationsReady: dep.isSuccess,
        setPendingBlastRadius,
        target: {
          schema: fromTable?.schema ?? null,
          objectName: fromTable?.name ?? null,
        },
      }),
    };
  }, [editContext, gateActive, setPendingBlastRadius, annotations, dep.isSuccess, fromTable?.schema, fromTable?.name]);
}
